import { CREATORBASE_ROOT_PATH } from 'Modules/Navigation/whitelabel/routes';
import { ActionCard, assets } from '@round/whitelabel-ui';
import styles from './ReportLink.module.css';
import { Link } from 'react-router-dom';
import useProjectId from '../../hooks/useProjectId';
import HeaderSummary from 'Modules/Plans/components/HeaderSummary/HeaderSummary';
import { VirtualList } from '@round/whitelabel-ui';
import { ControlledPopover, Image } from '@round/ui-kit';
import { useState } from 'react';
import useOnClickOutside from 'Hooks/useOnClickOutside';
import CreateReportModal from 'Modules/Plans/Reports/CreateReportModal/CreateReportModal';
import useProjectDetails from '../../useProjectDetails';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { useCurrentCreatorbaseUser } from 'Modules/Auth/hooks/useCurrentCreatorbaseUser';
import { useInfiniteReports, useInvalidateInfiniteReports, useUser } from '@round/creatorbase-helpers/api-hooks';
import { mapUserToOption } from 'Modules/Plans/User/hooks/useUsersSelect';

const { Chart: ChartIcon, Plus: PlusIcon } = assets;

const getReportLink = (reportId: string) => `/${CREATORBASE_ROOT_PATH}/reports/${reportId}/`;

type Props = {
    className?: string;
};

const ReportLink = ({ className }: Props) => {
    const projectId = useProjectId();

    const { project, team, brand, isLoading, isBrandLoading, isTeamLoading } = useProjectDetails();
    const areProjectDetailsLoading = isLoading || isBrandLoading || isTeamLoading;

    const { getCurrencyOptionsById } = useCurrencies();
    const { creatorbaseUserId } = useCurrentCreatorbaseUser();
    const { data: user } = useUser(creatorbaseUserId);

    const { data, isFetching, isFetchingNextPage, isPending, hasNextPage, fetchNextPage } = useInfiniteReports(
        { project_id: projectId, page_size: 10 },
        { enabled: typeof projectId === 'number' }
    );
    const invalidateInfiniteReports = useInvalidateInfiniteReports();

    const reports = data?.pages.flatMap((page) => page.results) ?? [];
    const [targetRef, setTargetRef] = useState<HTMLDivElement | null>(null);
    const [popoutRef, setPopoutRef] = useState<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const [isCreateReportModalOpen, setIsCreateReportModalOpen] = useState(false);

    useOnClickOutside(popoutRef, () => setIsOpen(false));

    if (areProjectDetailsLoading || isPending) {
        return (
            <HeaderSummary.Item className={className}>
                <HeaderSummary.Label>Report</HeaderSummary.Label>
                <HeaderSummary.Value className={styles.loadingContainer}>
                    <ActionCard Icon={ChartIcon} title="Report" isLoading={true} className={styles.loadingCard} />
                </HeaderSummary.Value>
            </HeaderSummary.Item>
        );
    }

    if (!reports.length) {
        return (
            <>
                <HeaderSummary.Item className={className}>
                    <HeaderSummary.Label>Report</HeaderSummary.Label>
                    <HeaderSummary.Value className={styles.createReportContainer}>
                        <ActionCard
                            title="Report"
                            Icon={PlusIcon}
                            onClick={() => {
                                setIsCreateReportModalOpen(true);
                            }}
                            className={styles.createReportCard}
                        />
                    </HeaderSummary.Value>
                </HeaderSummary.Item>

                <CreateReportModal
                    isOpen={isCreateReportModalOpen}
                    onClose={() => setIsCreateReportModalOpen(false)}
                    initialValues={{
                        name: project && brand ? `${brand.name} - ${project.name}` : '',
                        project_ids: projectId ? [projectId] : [],
                        team: team ? { value: team.id, label: team.name } : null,
                        currency: project?.currency_id ? getCurrencyOptionsById(project.currency_id) : null,
                        assignees: user ? [mapUserToOption(user)] : [],
                    }}
                    onCreated={() => invalidateInfiniteReports()}
                />
            </>
        );
    }

    if (reports.length === 1) {
        const [report] = reports;
        return (
            <HeaderSummary.Item className={className}>
                <HeaderSummary.Label>Report</HeaderSummary.Label>
                <HeaderSummary.Value>
                    <Link to={getReportLink(report.id)} className={styles.link}>
                        <ActionCard
                            title={report.name}
                            subTitle={report.description || '-'}
                            actionLabel="View Report"
                            Icon={ChartIcon}
                        />
                    </Link>
                </HeaderSummary.Value>
            </HeaderSummary.Item>
        );
    }

    const count = data?.pages[0].count;
    return (
        <HeaderSummary.Item className={className}>
            <HeaderSummary.Label>Reports</HeaderSummary.Label>
            <HeaderSummary.Value>
                <ActionCard
                    ref={setTargetRef}
                    className={styles.card}
                    title={`${count} reports`}
                    subTitle="View Reports"
                    Icon={ChartIcon}
                    onClick={() => setIsOpen((isOpen) => !isOpen)}
                />

                <ControlledPopover
                    isOpen={isOpen}
                    anchorElement={targetRef}
                    options={{
                        placement: 'bottom-start',
                        modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
                    }}
                    overlayClassName={styles.overlay}
                >
                    <div ref={setPopoutRef}>
                        <VirtualList
                            isLoading={isFetching}
                            className={styles.dropdown}
                            loadingIndicatorClassName={styles.loadingIndicator}
                            data={reports}
                            options={{ count: reports.length, estimateSize: () => 30, gap: 8 }}
                            onEndReached={() => {
                                if (!isFetchingNextPage && hasNextPage) {
                                    fetchNextPage();
                                }
                            }}
                        >
                            {(report) => (
                                <Link to={getReportLink(report.id)} className={styles.link}>
                                    <div className={styles.listItem}>
                                        <Image
                                            skeletonCircle={false}
                                            className={styles.reportImage}
                                            src={report.image || ''}
                                        />

                                        <div className={styles.reportTitles}>
                                            <p className={styles.reportName}>{report.name}</p>
                                            <p className={styles.reportDescription}>{report.description || '-'}</p>
                                        </div>
                                    </div>
                                </Link>
                            )}
                        </VirtualList>
                    </div>
                </ControlledPopover>
            </HeaderSummary.Value>
        </HeaderSummary.Item>
    );
};

export default ReportLink;
